// เช็คนักเรียนขาดสอบ — ครูผู้คุมสอบดูจากใบเซ็นชื่อ แล้วกดเฉพาะคนที่ขาด/ลา/สาย
//
// ขั้นตอนเหมือนเช็คชื่อคาบเรียน (teaching/CheckAttendance): เลือกชั้น → เลือกห้อง → เลือกวิชา →
// รายชื่อทั้งห้องขึ้นมาเป็น "มา" ทุกคน (ยกเว้นคนที่มีใบลาครอบวันนี้ขึ้นเป็น "ลา") แล้วบันทึก
// ครูทุกคนเข้าได้ ไม่มีการมอบหมายผู้คุมสอบ — ครูเลือกห้อง/วิชาที่ตัวเองคุมเอง
//
// วันหนึ่งสอบหลายวิชา หน่วยที่เช็คจึงเป็นห้องต่อวิชา วิชาที่ห้องนั้นเช็คไปแล้วในช่วงสอบนี้
// ไม่อยู่ในตัวเลือกอีก แต่ขึ้นเป็นรายการ "เช็คแล้ว" ข้างล่างให้เปิดดู/แก้ได้
//
// ใช้ได้เฉพาะวันสอบของวันนี้ หมดช่วงสอบแล้วล็อก ส่วนงานวัดผลและวิชาการสามัญเลือกวันย้อนหลัง
// และแก้ได้ตลอด (ตรวจซ้ำที่ server ไม่ได้พึ่งหน้าจอ)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Form, Spinner, Table } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { useLoading } from '../contexts/LoadingContext';
import { toISODate } from '../utils/thaiDate';
import { StudentAvatar, LeaveBadge, useIsMobile } from '../teaching/attendanceParts';
import jawi from '../locales/islamic/jawi';
import { API, STATUS, STATUS_KEYS, apiError, dayLabel, eventName, eventRange, useExamEvents } from './examAbsenceUtils';

const draftKey = (calId, rId, subId) => `exam_absence_${calId}_${rId}_${subId}`;
const readDraft = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
};
const writeDraft = (key, value) => {
  try {
    if (value) localStorage.setItem(key, JSON.stringify(value));
    else localStorage.removeItem(key);
  } catch { /* โหมดส่วนตัว — ไม่มีร่างก็ยังเช็คได้ */ }
};

// โหมด RTL ของสอบศาสนาใช้คำยาวีชุดเดียวกับหน้าเช็คชื่อภาคศาสนา (locales/islamic/jawi.js)
// ครูศาสนาเห็นคำเดียวกันทั้งสองหน้า ไม่ใช่อาหรับมาตรฐานชุดใหม่ที่ต้องเรียนอีกรอบ
const JAWI = jawi.checkAttendance;
const JAWI_STATUS = { present: JAWI.statusPresent, late: JAWI.statusLate, leave: JAWI.statusLeave, absent: JAWI.statusAbsent };

const StatusButtons = ({ value, onChange, disabled, rtl = false, size = 'sm' }) => (
  <ButtonGroup>
    {STATUS_KEYS.map((key) => (
      <Button key={key} size={size} disabled={disabled}
        variant={value === key ? STATUS[key].variant : `outline-${STATUS[key].variant}`}
        onClick={() => onChange(key)}
        lang={rtl ? 'ar' : undefined} className={rtl ? 'exam-arabic' : undefined}
        title={rtl ? STATUS[key].long : undefined}>
        {rtl ? JAWI_STATUS[key] : STATUS[key].text}
      </Button>
    ))}
  </ButtonGroup>
);

const timeText = (dt) => (dt ? dt.slice(11, 16) : '');

// สอบศาสนา — แสดงชื่ออาหรับคู่ชื่อไทย ครูศาสนาคุ้นกับชื่ออาหรับมากกว่า และใบเซ็นชื่อสอบศาสนา
// เขียนเป็นอาหรับ ใช้ค่าจำภาษาตัวเดียวกับเมนูภาคศาสนา ('islamic-lang') ครูที่ตั้งยาวีไว้แล้ว
// จะได้รายชื่อแบบขวาไปซ้ายทันที
//
// RTL กลับทิศเฉพาะกล่องรายชื่อ ไม่ใช่ทั้งหน้า — หน้านี้อยู่ใต้ layout ปกติ ถ้าตั้ง dir ที่ <html>
// แถบข้างและหัวหน้าจะกลับด้านไปด้วย และค้างอยู่ตอนออกจากหน้า (IslamicLayout เป็นคนคืนค่าให้
// เฉพาะหน้าใต้ /islamic)
const readRtl = () => {
  try { return localStorage.getItem('islamic-lang') === 'jawi'; } catch { return false; }
};
const writeRtl = (on) => {
  try { localStorage.setItem('islamic-lang', on ? 'jawi' : 'th'); } catch { /* ไม่จำก็ได้ */ }
};

// ชื่อนักเรียน: ภาคสามัญเป็นชื่อไทยอย่างเดียว ภาคศาสนาแสดงสองภาษา ภาษาหลักตามโหมด
// คนที่ยังไม่มีชื่ออาหรับแสดงชื่อไทยอย่างเดียว ไม่ปล่อยว่าง
const StudentNames = ({ student, islamic, rtl, className = '' }) => {
  const arabic = islamic && student.st_arabicname
    ? <span className="exam-arabic" lang="ar" dir="rtl">{student.st_arabicname}</span>
    : null;
  if (!arabic) return <span className={className}>{student.st_name}</span>;
  return rtl ? (
    <span className={className}>
      <span className="d-block">{arabic}</span>
      <small className="d-block text-secondary fw-normal">{student.st_name}</small>
    </span>
  ) : (
    <span className={className}>
      <span className="d-block">{student.st_name}</span>
      <small className="d-block text-secondary fw-normal">{arabic}</small>
    </span>
  );
};

/* ── รายชื่อในห้อง ────────────────────────────────────────────────────────── */

function RosterView({ ev, date, rId, subId, onBack, onSaved }) {
  const { setLoading } = useLoading();
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState(null);
  const view = viewMode ?? (isMobile ? 'card' : 'table');

  const islamic = ev.track === 'islamic';
  const [rtlPref, setRtlPref] = useState(readRtl);
  const rtl = islamic && rtlPref;
  const toggleRtl = () => setRtlPref((v) => { writeRtl(!v); return !v; });

  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [saving, setSaving] = useState(false);
  const key = draftKey(ev.cal_id, rId, subId);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axiosInstance.get(`${API}/exam-absence/roster`, { params: { cal_id: ev.cal_id, date, r_id: rId, sub_id: subId } })
      .then(({ data: res }) => {
        if (!alive) return;
        const initial = Object.fromEntries(res.students.map((s) => [s.study_id, s.status]));
        // ร่างที่ครูกดค้างไว้ (รีเฟรช/จอดับ) ชนะค่าจาก server เฉพาะตอนที่ยังบันทึกได้
        const draft = res.canWrite ? readDraft(key) : null;
        if (draft) {
          for (const s of res.students) if (STATUS[draft[s.study_id]]) initial[s.study_id] = draft[s.study_id];
        }
        setData(res);
        setStatuses(initial);
      })
      .catch((err) => Swal.fire({ icon: 'error', title: 'โหลดรายชื่อไม่สำเร็จ', text: apiError(err) }).then(onBack))
      .finally(() => setLoading(false));
    return () => { alive = false; };
  }, [ev.cal_id, date, rId, subId, key, setLoading, onBack]);

  const setStatus = (studyId, status) => {
    setStatuses((prev) => {
      const next = { ...prev, [studyId]: status };
      writeDraft(key, next);
      return next;
    });
  };

  const counts = useMemo(() => {
    const c = { present: 0, late: 0, leave: 0, absent: 0 };
    for (const v of Object.values(statuses)) c[v] = (c[v] || 0) + 1;
    return c;
  }, [statuses]);

  const save = async () => {
    if (saving) return;
    const notPresent = data.students.filter((s) => statuses[s.study_id] !== 'present');
    const confirm = await Swal.fire({
      icon: 'question',
      title: `ห้อง ${data.room.label} · ${data.subject.label}`,
      html: `นักเรียน ${data.students.length} คน · ขาด <b>${counts.absent}</b> · ลา <b>${counts.leave}</b> · สาย <b>${counts.late}</b>`
        + (notPresent.length ? '' : '<br><span class="text-success">มาสอบครบทุกคน</span>'),
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ตรวจอีกครั้ง',
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    setLoading(true);
    try {
      await axiosInstance.post(`${API}/exam-absence/check`, {
        cal_id: ev.cal_id,
        // วิชาที่เช็คไปแล้วผูกกับวันที่สอบจริง ส่งวันของการเช็คนั้นกลับไป
        date: data.date,
        r_id: rId,
        sub_id: subId,
        students: data.students.map((s) => ({ study_id: s.study_id, status: statuses[s.study_id] })),
      });
      writeDraft(key, null);
      await Swal.fire({ icon: 'success', title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false });
      onSaved();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: apiError(err) });
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  if (!data) return null;
  const locked = !data.canWrite;
  const unsaved = data.students.filter((s) => s.unsaved).length;

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button variant="outline-secondary" size="sm" onClick={onBack}>
          <i className="ri-arrow-left-line"></i> เลือกวิชาอื่น
        </Button>
        <h5 className="mb-0">ห้อง {data.room.label} · {data.subject.label}</h5>
        <span className="text-secondary fs-sm">สอบวันที่ {dayLabel(data.date, { short: false })}</span>
      </div>

      {data.check && (
        <Alert variant="success" className="py-2 fs-sm">
          <i className="ri-checkbox-circle-line me-1"></i>
          เช็คแล้วโดย {data.check.checked_by_name} เวลา {timeText(data.check.checked_at)} น.
          {data.check.updated_by_name && <> · แก้ล่าสุดโดย {data.check.updated_by_name} เวลา {timeText(data.check.updated_at)} น.</>}
          {!locked && <> — กดบันทึกอีกครั้งเพื่อแก้ไข</>}
        </Alert>
      )}
      {unsaved > 0 && (
        <Alert variant="warning" className="py-2 fs-sm">
          มีนักเรียน {unsaved} คนที่ย้ายเข้าห้องนี้หลังจากเช็คไปแล้ว ขึ้นเป็น "มา" ไว้ก่อน — ตรวจแล้วบันทึกอีกครั้ง
        </Alert>
      )}
      {locked && (
        <Alert variant="secondary" className="py-2 fs-sm">
          <i className="ri-lock-line me-1"></i> ดูได้อย่างเดียว — บันทึกได้เฉพาะวันสอบของวันนี้
        </Alert>
      )}

      <Card className="card-one">
        <Card.Body className={`p-3${rtl ? ' exam-rtl' : ''}`} dir={rtl ? 'rtl' : undefined}>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div className="d-flex flex-wrap gap-2">
              {STATUS_KEYS.map((k) => (
                <Badge key={k} bg={STATUS[k].variant} className="fs-sm fw-normal">
                  <span className={rtl ? 'exam-arabic' : undefined}>{rtl ? JAWI_STATUS[k] : STATUS[k].long}</span> {counts[k] || 0}
                </Badge>
              ))}
            </div>
            <div className="d-flex flex-wrap gap-2">
            {islamic && (
              <Button size="sm" variant={rtl ? 'dark' : 'outline-dark'} onClick={toggleRtl}
                title={rtl ? 'แสดงแบบซ้ายไปขวา ชื่อไทยก่อน' : 'แสดงแบบขวาไปซ้าย ชื่ออาหรับก่อน'}>
                {rtl ? 'ไทย' : 'ياوي'} <i className={rtl ? 'ri-text-direction-l' : 'ri-text-direction-r'}></i>
              </Button>
            )}
            <ButtonGroup size="sm">
              <Button variant={view === 'card' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('card')}>
                <i className="ri-layout-grid-line"></i> การ์ด
              </Button>
              <Button variant={view === 'table' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('table')}>
                <i className="ri-table-line"></i> ตาราง
              </Button>
            </ButtonGroup>
            </div>
          </div>

          {view === 'table' ? (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 50 }}>{rtl ? jawi.attendanceStat.colNo : 'เลขที่'}</th>
                  <th className="text-center" style={{ width: 100 }}>{rtl ? JAWI.colId : 'เลขประจำตัว'}</th>
                  <th className={rtl ? 'text-end' : undefined}>{rtl ? JAWI.colName : 'ชื่อ-สกุล'}</th>
                  <th className="text-center" style={{ width: 180 }}>{rtl ? JAWI.colStatus : 'การเข้าสอบ'}</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((s, i) => (
                  <tr key={s.study_id} className={statuses[s.study_id] === 'absent' ? 'table-danger' : ''}>
                    <th className="text-center">{i + 1}</th>
                    <td className="text-center">{s.st_idnumber}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <StudentAvatar photo={s.st_photo} name={s.st_name} />
                        <span>
                          <StudentNames student={s} islamic={islamic} rtl={rtl} />
                          <LeaveBadge leave={s.leave} />
                        </span>
                      </div>
                    </td>
                    <td className="text-center">
                      <StatusButtons value={statuses[s.study_id]} disabled={locked} rtl={rtl} onChange={(v) => setStatus(s.study_id, v)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="d-flex flex-column gap-2">
              {data.students.map((s, i) => (
                <Card key={s.study_id} className={`border ${statuses[s.study_id] === 'absent' ? 'border-danger bg-danger bg-opacity-10' : ''}`}>
                  <Card.Body className="p-0" style={{ minHeight: 76 }}>
                    <div className="d-flex" style={{ minHeight: 76 }}>
                      <div style={{ width: 64, flexShrink: 0, overflow: 'hidden', borderRadius: rtl ? '0 calc(0.375rem - 1px) calc(0.375rem - 1px) 0' : 'calc(0.375rem - 1px) 0 0 calc(0.375rem - 1px)' }}>
                        <StudentAvatar photo={s.st_photo} name={s.st_name} fill />
                      </div>
                      <div className="flex-grow-1 px-2 py-2 d-flex flex-column justify-content-between">
                        <div className="d-flex align-items-start gap-2">
                          <span className="badge bg-secondary flex-shrink-0 mt-1">{i + 1}</span>
                          <div className="flex-grow-1 text-end">
                            <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                              <StudentNames student={s} islamic={islamic} rtl={rtl} />
                            </div>
                            <small className="text-muted">เลขประจำตัว {s.st_idnumber}</small>
                            {s.leave && <div className="mt-1"><LeaveBadge leave={s.leave} /></div>}
                          </div>
                        </div>
                        <div className="d-flex justify-content-end">
                          <StatusButtons value={statuses[s.study_id]} disabled={locked} rtl={rtl} onChange={(v) => setStatus(s.study_id, v)} />
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}

          {!locked && (
            <div className="d-flex justify-content-end mt-3">
              <Button variant="success" onClick={save} disabled={saving}>
                <i className="ri-save-3-fill"></i> {saving ? 'กำลังบันทึก...' : 'บันทึกการเข้าสอบ'}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}

/* ── เลือกชั้น / ห้อง ─────────────────────────────────────────────────────── */

function RoomPicker({ ev, date, classId, onClass, onRoom, reloadKey }) {
  const [state, setState] = useState({ loading: true, rooms: [], canWrite: false, error: null });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    axiosInstance.get(`${API}/exam-absence/rooms`, { params: { cal_id: ev.cal_id, date } })
      .then(({ data }) => alive && setState({ loading: false, error: null, rooms: data.rooms, canWrite: data.canWrite }))
      .catch((err) => alive && setState({ loading: false, rooms: [], canWrite: false, error: apiError(err) }));
    return () => { alive = false; };
  }, [ev.cal_id, date, reloadKey]);

  const classes = useMemo(() => {
    const map = new Map();
    for (const r of state.rooms) {
      const c = map.get(r.c_id) || { c_id: r.c_id, label: r.class_label, total: 0, checked: 0 };
      c.total += 1;
      if (r.subjects_checked) c.checked += 1;
      map.set(r.c_id, c);
    }
    return [...map.values()].sort((a, b) => a.c_id - b.c_id);
  }, [state.rooms]);

  if (state.loading) return <div className="text-center py-4"><Spinner animation="border" size="sm" /> กำลังโหลดห้อง...</div>;
  if (state.error) return <Alert variant="danger">{state.error}</Alert>;
  if (!state.rooms.length) return <Alert variant="secondary">ไม่มีห้องที่ต้องสอบในช่วงนี้</Alert>;

  const current = classes.find((c) => c.c_id === classId) ? classId : null;
  const rooms = state.rooms.filter((r) => r.c_id === current);

  return (
    <>
      {!state.canWrite && (
        <Alert variant="secondary" className="py-2 fs-sm"><i className="ri-lock-line me-1"></i> วันนี้บันทึกไม่ได้ — ดูได้อย่างเดียว</Alert>
      )}
      <Card className="card-one mb-3">
        <Card.Body className="p-3">
          <div className="fw-semibold mb-2">1. เลือกชั้น</div>
          <div className="d-flex flex-wrap gap-2">
            {classes.map((c) => (
              <Button key={c.c_id} variant={current === c.c_id ? 'primary' : 'outline-primary'} onClick={() => onClass(c.c_id)}
                style={{ minWidth: 88 }}>
                {c.label}
                <div className="fs-xs opacity-75">วันนี้เช็คแล้ว {c.checked}/{c.total} ห้อง</div>
              </Button>
            ))}
          </div>
        </Card.Body>
      </Card>

      {current && (
        <Card className="card-one">
          <Card.Body className="p-3">
            <div className="fw-semibold mb-2">2. เลือกห้องที่คุมสอบ</div>
            <div className="d-grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))' }}>
              {rooms.map((r) => (
                <Button key={r.r_id} variant={r.subjects_checked ? 'outline-success' : 'outline-secondary'} className="py-2"
                  onClick={() => onRoom(r.r_id, r.label)}>
                  <div className="fw-semibold">{r.label}</div>
                  <div className="fs-xs">
                    {r.subjects_checked
                      ? <>เช็คแล้ว {r.subjects_checked} วิชา{r.absent ? <> · <span className="text-danger">ขาด {r.absent}</span></> : ''}</>
                      : 'ยังไม่เช็ค'}
                  </div>
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
}

/* ── เลือกวิชา ─────────────────────────────────────────────────────────────── */

function SubjectPicker({ ev, rId, roomLabel, onSubject, onBack, reloadKey }) {
  const [state, setState] = useState({ loading: true, options: [], checked: [], error: null });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    axiosInstance.get(`${API}/exam-absence/subjects`, { params: { cal_id: ev.cal_id, r_id: rId } })
      .then(({ data }) => alive && setState({ loading: false, error: null, options: data.options, checked: data.checked }))
      .catch((err) => alive && setState({ loading: false, options: [], checked: [], error: apiError(err) }));
    return () => { alive = false; };
  }, [ev.cal_id, rId, reloadKey]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button variant="outline-secondary" size="sm" onClick={onBack}>
          <i className="ri-arrow-left-line"></i> เลือกห้องอื่น
        </Button>
        <h5 className="mb-0">ห้อง {roomLabel || ''}</h5>
      </div>

      {state.loading && <div className="text-center py-4"><Spinner animation="border" size="sm" /> กำลังโหลดวิชา...</div>}
      {state.error && <Alert variant="danger">{state.error}</Alert>}

      {!state.loading && !state.error && (
        <>
          <Card className="card-one mb-3">
            <Card.Body className="p-3">
              <div className="fw-semibold mb-2">3. เลือกวิชาที่สอบ</div>
              {state.options.length === 0 ? (
                <div className="text-secondary fs-sm">
                  {state.checked.length ? 'เช็คครบทุกวิชาของห้องนี้แล้ว' : 'ไม่พบวิชาของห้องนี้ — ติดต่องานวิชาการให้จัดครูสอนของห้องก่อน'}
                </div>
              ) : (
                <div className="d-grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                  {state.options.map((s) => (
                    <Button key={s.sub_id} variant="outline-primary" className="text-start py-2" onClick={() => onSubject(s.sub_id)}>
                      {s.label}
                    </Button>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {state.checked.length > 0 && (
            <Card className="card-one">
              <Card.Header><Card.Title as="h6" className="mb-0">วิชาที่เช็คแล้ว ({state.checked.length})</Card.Title></Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle fs-sm">
                  <tbody>
                    {state.checked.map((c) => (
                      <tr key={c.sub_id} role="button" onClick={() => onSubject(c.sub_id)}>
                        <td>
                          <div className="fw-semibold">{c.label}</div>
                          <div className="fs-xs text-secondary">{dayLabel(c.date)} · {c.checked_by_name} {timeText(c.checked_at)} น.</div>
                        </td>
                        <td className="text-end text-nowrap">
                          {c.absent ? <Badge bg="danger" className="me-1">ขาด {c.absent}</Badge> : <Badge bg="success" className="me-1">มาครบ</Badge>}
                          {c.on_leave > 0 && <Badge bg="info" className="me-1">ลา {c.on_leave}</Badge>}
                          {c.late > 0 && <Badge bg="warning" className="me-1">สาย {c.late}</Badge>}
                          <i className={c.canWrite ? 'ri-edit-line text-primary' : 'ri-eye-line text-secondary'}></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </>
  );
}

/* ── หน้า ────────────────────────────────────────────────────────────────── */

export default function ExamAbsenceCheck() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [params, setParams] = useSearchParams();
  const { loading, events, error } = useExamEvents();
  const today = toISODate(new Date());
  const [reloadKey, setReloadKey] = useState(0);

  // ตัวเลือกช่วงสอบ = เฉพาะที่ครอบวันนี้ (วันสอบสามัญไม่มีตัวเลือกของศาสนา และกลับกัน)
  // เจ้าของข้อมูลนอกช่วงสอบได้ช่วงสอบย้อนหลังของภาคตัวเองแทน เพื่อแก้ย้อนหลังได้
  // มีตัวเลือกเดียว = เลือกให้เอง; สองภาคสอบวันเดียวกัน (เกิดยาก) = ต้องเลือกเองก่อน ไม่เดาให้
  // เพราะเลือกผิดภาค ห้องและรายชื่อจะเป็นของอีกภาคทั้งหมด
  const choices = useMemo(() => {
    const active = events.filter((e) => e.start_date <= today && e.end_date >= today);
    return active.length ? active : events.filter((e) => e.canManage);
  }, [events, today]);
  const ev = useMemo(() => {
    if (choices.length === 1) return choices[0];
    return choices.find((e) => String(e.cal_id) === params.get('cal')) || null;
  }, [choices, params]);
  const mustChoose = choices.length > 1 && !ev;
  // ครูทั่วไปเช็คได้แค่วันนี้ งานวัดผล/วิชาการเลือกวันไหนของช่วงสอบก็ได้
  const date = ev
    ? (ev.canManage && ev.days.includes(params.get('date')) ? params.get('date') : (ev.days.includes(today) ? today : ev.days[0]))
    : null;
  const classId = Number(params.get('class')) || null;
  const rId = Number(params.get('room')) || null;
  const subId = Number(params.get('sub')) || null;
  const [roomLabels, setRoomLabels] = useState({});

  const update = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '') next.delete(k); else next.set(k, String(v));
      }
      return next;
    });
  }, [setParams]);

  const backToRooms = useCallback(() => update({ room: null, sub: null }), [update]);
  const backToSubjects = useCallback(() => update({ sub: null }), [update]);
  // บันทึกแล้วกลับไปที่ตัวเลือกวิชาของห้องเดิม — ผู้คุมห้องเดียวกันมักคุมวิชาถัดไปต่อ
  const afterSave = useCallback(() => { setReloadKey((k) => k + 1); update({ sub: null }); }, [update]);

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">เช็คนักเรียนขาดสอบ</h4>
          <div className="fs-xs text-secondary">เลือกชั้น ห้อง และวิชาที่คุมสอบ ทุกคนขึ้นเป็น "มา" ไว้ก่อน ดูจากใบเซ็นชื่อแล้วกดเฉพาะคนที่ขาด ลา หรือสาย</div>
        </div>

        {loading && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {mustChoose && (
          <Card className="card-one mb-3 border-warning">
            <Card.Body className="p-3">
              <div className="fw-semibold mb-2">
                <i className="ri-error-warning-line text-warning me-1"></i>
                {choices.some((e) => e.start_date <= today && e.end_date >= today) && new Set(choices.map((e) => e.track)).size > 1
                  ? 'วันนี้มีทั้งสอบสามัญและสอบศาสนา — เลือกช่วงสอบที่คุมก่อน'
                  : 'เลือกช่วงสอบก่อน'}
              </div>
              <Form.Select style={{ maxWidth: 420 }} value="" isInvalid
                onChange={(e) => update({ cal: e.target.value, date: null, class: null, room: null, sub: null })}>
                <option value="" disabled>— เลือกช่วงสอบ —</option>
                {choices.map((e) => <option key={e.cal_id} value={e.cal_id}>{eventName(e)} ({eventRange(e)})</option>)}
              </Form.Select>
            </Card.Body>
          </Card>
        )}

        {!loading && !error && !ev && !mustChoose && (
          <Alert variant="secondary">
            <i className="ri-calendar-close-line me-1"></i>
            วันนี้ไม่อยู่ในช่วงสอบ — เมนูนี้เปิดเฉพาะวันที่ปฏิทินระบุว่าเป็นวันสอบ
          </Alert>
        )}

        {ev && (
          <>
            <Card className="card-one mb-3">
              <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-3">
                {choices.length > 1 ? (
                  <Form.Select size="sm" style={{ maxWidth: 360 }} value={ev.cal_id}
                    onChange={(e) => update({ cal: e.target.value, date: null, class: null, room: null, sub: null })}>
                    {choices.map((e) => <option key={e.cal_id} value={e.cal_id}>{eventName(e)} ({eventRange(e)})</option>)}
                  </Form.Select>
                ) : (
                  <div>
                    <div className="fw-semibold">{eventName(ev)}</div>
                    <div className="fs-xs text-secondary">{eventRange(ev)}</div>
                  </div>
                )}
                {ev.canManage ? (
                  <ButtonGroup size="sm" className="flex-wrap">
                    {ev.days.map((d) => (
                      <Button key={d} variant={d === date ? 'primary' : 'outline-primary'} onClick={() => update({ date: d, room: null, sub: null })}>
                        {dayLabel(d)}{d === today ? ' (วันนี้)' : ''}
                      </Button>
                    ))}
                  </ButtonGroup>
                ) : (
                  <Badge bg="primary" className="fs-sm fw-normal">วันสอบ {dayLabel(date, { short: false })}</Badge>
                )}
              </Card.Body>
            </Card>

            {rId && subId ? (
              <RosterView key={`${ev.cal_id}-${date}-${rId}-${subId}`} ev={ev} date={date} rId={rId} subId={subId}
                onBack={backToSubjects} onSaved={afterSave} />
            ) : rId ? (
              <SubjectPicker ev={ev} rId={rId} roomLabel={roomLabels[rId]} reloadKey={reloadKey}
                onSubject={(sub) => update({ sub })} onBack={backToRooms} />
            ) : (
              <RoomPicker ev={ev} date={date} classId={classId} reloadKey={reloadKey}
                onClass={(c) => update({ class: c })}
                onRoom={(r, label) => { setRoomLabels((m) => ({ ...m, [r]: label })); update({ room: r }); }} />
            )}
          </>
        )}

        <Footer />
      </div>
    </>
  );
}
